import React, { useEffect, useRef, useState } from "react";

/* ------------------------------------------------------------------
   Animated pieces of the /status page.

   <ConnectionMap>  — your browser, the Node server and the database as
     nodes. Every real ping (SiteStatus samples every few seconds) launches a
     packet that arcs over to the server and back underneath; its speed and
     color come from the measured round trip, and a failed ping fizzles out
     in red before reaching the server. The server ring breathes in the
     overall status color; the server→database link flows while the database
     reports connected and goes dashed red when it doesn't.
   <PingHistory>    — the last ~5 minutes of pings as a strip of bars.
   <TweenNumber>    — a number that glides to its new value.

   Packets are drawn imperatively (refs + requestAnimationFrame) so a ping
   doesn't re-render the page 60 times a second, and the loop only runs while
   a packet is in flight. Honors prefers-reduced-motion.
------------------------------------------------------------------ */

const SVG_NS = "http://www.w3.org/2000/svg";

// viewBox geometry
const VB_W = 720;
const VB_H = 240;
const NODE_R = 40;
const NODES = {
  you: { x: 110, y: 120 },
  server: { x: 360, y: 120 },
  db: { x: 610, y: 120 },
};
// Request goes over the top, response comes back underneath.
const REQ_PATH = "M 148 106 C 215 30, 295 30, 322 106";
const RES_PATH = "M 322 134 C 295 210, 215 210, 148 134";
const DB_REQ_PATH = "M 398 106 C 465 30, 545 30, 572 106";
const DB_RES_PATH = "M 572 134 C 545 210, 465 210, 398 134";

export const LATENCY_TONES = {
  fast: { label: "Fast", max: 150, color: "#10b981", bar: "bg-emerald-500" },
  ok: { label: "OK", max: 750, color: "#38bdf8", bar: "bg-sky-400" },
  slow: { label: "Slow", max: Infinity, color: "#f59e0b", bar: "bg-amber-500" },
  fail: { label: "No response", color: "#f43f5e", bar: "bg-rose-500" },
};

export const toneFor = (ms) =>
  ms == null
    ? "fail"
    : ms < LATENCY_TONES.fast.max
    ? "fast"
    : ms < LATENCY_TONES.ok.max
    ? "ok"
    : "slow";

const STATUS_COLORS = {
  operational: "#10b981",
  degraded: "#f59e0b",
  down: "#f43f5e",
  checking: "#94a3b8",
};

const easeInOut = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Scoped keyframes for the SVG bits (transform-box keeps scaling centered on
// each circle rather than the SVG origin).
const STYLES = `
.sp-breathe { transform-box: fill-box; transform-origin: center; animation: sp-breathe 2.6s ease-in-out infinite; }
.sp-ripple { transform-box: fill-box; transform-origin: center; animation: sp-ripple 0.9s ease-out forwards; }
.sp-burst { transform-box: fill-box; transform-origin: center; animation: sp-burst 0.7s ease-out forwards; }
.sp-flow { stroke-dasharray: 2 12; animation: sp-flow 1.1s linear infinite; }
.sp-flow-back { stroke-dasharray: 2 12; animation: sp-flow 1.1s linear infinite reverse; }
.sp-bar-in { transform-origin: bottom; animation: sp-bar-in 0.45s cubic-bezier(.2,.9,.3,1.3); }
.sp-orbit { transform-box: view-box; transform-origin: 360px 120px; animation: sp-orbit 9s linear infinite; }
@keyframes sp-breathe { 0%,100% { transform: scale(1); opacity: .55 } 50% { transform: scale(1.1); opacity: .18 } }
@keyframes sp-ripple { from { transform: scale(1); opacity: .7 } to { transform: scale(1.75); opacity: 0 } }
@keyframes sp-burst { from { transform: scale(.4); opacity: 1 } to { transform: scale(2.6); opacity: 0 } }
@keyframes sp-flow { to { stroke-dashoffset: -28 } }
@keyframes sp-bar-in { from { transform: scaleY(0); opacity: 0 } to { transform: scaleY(1); opacity: 1 } }
@keyframes sp-orbit { to { transform: rotate(360deg) } }
@media (prefers-reduced-motion: reduce) {
  .sp-breathe, .sp-flow, .sp-flow-back, .sp-bar-in, .sp-orbit { animation: none; }
}
`;

// ---------------------------------------------------------------- icons
// Drawn centered on (0,0), stroked in currentColor.
const LaptopIcon = () => (
  <g fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="-15" y="-13" width="30" height="20" rx="3" />
    <path d="M -20 12 H 20" />
  </g>
);

const ServerIcon = () => (
  <g fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinejoin="round">
    <rect x="-15" y="-16" width="30" height="9" rx="2.5" />
    <rect x="-15" y="-4.5" width="30" height="9" rx="2.5" />
    <rect x="-15" y="7" width="30" height="9" rx="2.5" />
    <g fill="currentColor" stroke="none">
      <circle cx="9" cy="-11.5" r="1.6" />
      <circle cx="9" cy="0" r="1.6" />
      <circle cx="9" cy="11.5" r="1.6" />
    </g>
  </g>
);

const DatabaseIcon = () => (
  <g fill="none" stroke="currentColor" strokeWidth="2.4">
    <ellipse cx="0" cy="-11" rx="14" ry="5" />
    <path d="M -14 -11 V 11 A 14 5 0 0 0 14 11 V -11" />
    <path d="M -14 0 A 14 5 0 0 0 14 0" />
  </g>
);

const Node = ({ x, y, ringColor, breathe, children }) => (
  <g transform={`translate(${x} ${y})`}>
    {breathe && (
      <circle
        className="sp-breathe"
        r={NODE_R + 10}
        fill="none"
        stroke={ringColor}
        strokeWidth="2"
      />
    )}
    <circle
      r={NODE_R}
      style={{ fill: "var(--app-card-bg)", stroke: ringColor }}
      strokeWidth="2.5"
      filter="url(#sp-shadow)"
    />
    {/* glassy top highlight */}
    <ellipse cx="0" cy={-NODE_R * 0.45} rx={NODE_R * 0.62} ry={NODE_R * 0.32} fill="url(#sp-sheen)" />
    <g style={{ color: "var(--app-text)" }}>{children}</g>
  </g>
);

// ---------------------------------------------------------------- map

export const ConnectionMap = ({ samples, status, dbOk, labels }) => {
  const reqRef = useRef(null);
  const resRef = useRef(null);
  const packetLayer = useRef(null);
  const fxLayer = useRef(null);
  const packets = useRef([]);
  const frame = useRef(null);
  const lastSeen = useRef(null);
  const [reduced] = useState(prefersReducedMotion);

  // Expanding ring (arrival) or burst (failure) at a point.
  const effect = (x, y, color, kind) => {
    const layer = fxLayer.current;
    if (!layer) return;
    const c = document.createElementNS(SVG_NS, "circle");
    c.setAttribute("cx", x);
    c.setAttribute("cy", y);
    c.setAttribute("r", kind === "burst" ? 10 : NODE_R);
    c.setAttribute("fill", kind === "burst" ? color : "none");
    c.setAttribute("fill-opacity", kind === "burst" ? "0.35" : "0");
    c.setAttribute("stroke", color);
    c.setAttribute("stroke-width", kind === "burst" ? "2" : "3");
    c.setAttribute("class", kind === "burst" ? "sp-burst" : "sp-ripple");
    layer.appendChild(c);
    const remove = () => c.remove();
    c.addEventListener("animationend", remove, { once: true });
    setTimeout(remove, 1500); // in case animationend never fires (hidden tab)
  };

  const tick = (now) => {
    const req = reqRef.current;
    const res = resRef.current;
    if (!req || !res) {
      frame.current = null;
      return;
    }
    const reqLen = req.getTotalLength();
    const resLen = res.getTotalLength();

    packets.current = packets.current.filter((p) => {
      const t = (now - p.start) / p.dur;
      // A failed ping gives up 85% of the way to the server.
      const end = p.fail ? 0.425 : 1;
      if (t >= end) {
        const last = p.els[0];
        if (p.fail) {
          effect(+last.getAttribute("cx"), +last.getAttribute("cy"), p.color, "burst");
        } else {
          effect(NODES.you.x, NODES.you.y, p.color, "ripple");
        }
        p.els.forEach((el) => el.remove());
        return false;
      }
      if (!p.fail && !p.arrived && t >= 0.5) {
        p.arrived = true;
        effect(NODES.server.x, NODES.server.y, p.color, "ripple");
      }
      p.els.forEach((el, i) => {
        const ti = Math.max(0, t - i * 0.022); // trailing dots
        const pt =
          ti < 0.5
            ? req.getPointAtLength(easeInOut(ti / 0.5) * reqLen)
            : res.getPointAtLength(easeInOut((ti - 0.5) / 0.5) * resLen);
        el.setAttribute("cx", pt.x);
        el.setAttribute("cy", pt.y);
      });
      return true;
    });

    frame.current = packets.current.length ? requestAnimationFrame(tick) : null;
  };

  const launch = (ms) => {
    const layer = packetLayer.current;
    if (!layer) return;
    const fail = ms == null;
    const color = LATENCY_TONES[toneFor(ms)].color;
    if (reduced) {
      // No travel animation — just mark the outcome where it happened.
      if (fail) effect(NODES.server.x - 70, NODES.server.y - 55, color, "burst");
      return;
    }
    // Real round trips are tens of ms — far too quick to see — so the travel
    // time is stretched, but slower pings still visibly take longer.
    const dur = fail ? 2200 : 900 + Math.min(ms, 1500) * 1.3;
    const els = [0, 1, 2, 3, 4].map((i) => {
      const c = document.createElementNS(SVG_NS, "circle");
      c.setAttribute("r", String(6.5 - i * 1.15));
      c.setAttribute("fill", color);
      c.setAttribute("opacity", String(i === 0 ? 1 : 0.6 - i * 0.11));
      c.setAttribute("cx", "-100");
      if (i === 0) c.setAttribute("filter", "url(#sp-glow)");
      layer.appendChild(c);
      return c;
    });
    packets.current.push({ start: performance.now(), dur, fail, color, els, arrived: false });
    if (!frame.current) frame.current = requestAnimationFrame(tick);
  };

  // Fire one packet per new sample (not on the initial backlog, and not
  // while the tab is hidden — those would all land at once on return).
  useEffect(() => {
    const latest = samples[samples.length - 1];
    if (!latest || latest.id === lastSeen.current) return;
    lastSeen.current = latest.id;
    if (document.visibilityState === "hidden") return;
    launch(latest.ms);
    // launch/tick only touch refs; re-running on their identity is pointless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [samples]);

  useEffect(
    () => () => {
      if (frame.current) cancelAnimationFrame(frame.current);
      packets.current.forEach((p) => p.els.forEach((el) => el.remove()));
      packets.current = [];
    },
    []
  );

  const serverColor = STATUS_COLORS[status] || STATUS_COLORS.checking;
  const down = status === "down";
  const dbColor = dbOk === false ? STATUS_COLORS.down : dbOk ? "#10b981" : "#94a3b8";
  const linkStroke = down ? STATUS_COLORS.down : "url(#sp-link)";

  return (
    <div>
      <style>{STYLES}</style>
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="block h-auto w-full overflow-visible"
        role="img"
        aria-label={`Connection diagram: your browser to the Node server is ${
          down ? "unreachable" : "connected"
        }; the server to the database is ${
          dbOk === false ? "not connected" : dbOk ? "connected" : "being checked"
        }.`}
      >
        <defs>
          <linearGradient id="sp-link" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#a78bfa" />
          </linearGradient>
          <radialGradient id="sp-sheen" cx="50%" cy="0%" r="80%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
          <filter id="sp-glow" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="sp-shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#1e2950" floodOpacity="0.18" />
          </filter>
        </defs>

        {/* Faint rails under the animated links */}
        {[REQ_PATH, RES_PATH, DB_REQ_PATH, DB_RES_PATH].map((d) => (
          <path key={d} d={d} fill="none" style={{ stroke: "var(--app-divider)" }} strokeWidth="6" strokeLinecap="round" />
        ))}

        {/* Browser ⇄ server */}
        <path ref={reqRef} d={REQ_PATH} fill="none" stroke={linkStroke} strokeWidth="2.5" strokeLinecap="round" className={down ? "" : "sp-flow"} strokeDasharray={down ? "6 8" : undefined} opacity={down ? 0.8 : 0.9} />
        <path ref={resRef} d={RES_PATH} fill="none" stroke={linkStroke} strokeWidth="2.5" strokeLinecap="round" className={down ? "" : "sp-flow"} strokeDasharray={down ? "6 8" : undefined} opacity={down ? 0.4 : 0.9} />

        {/* Server ⇄ database */}
        {[DB_REQ_PATH, DB_RES_PATH].map((d) => (
          <path
            key={d}
            d={d}
            fill="none"
            stroke={dbColor}
            strokeWidth="2.5"
            strokeLinecap="round"
            className={dbOk ? "sp-flow" : ""}
            strokeDasharray={dbOk ? undefined : "6 8"}
            opacity={dbOk ? 0.85 : 0.6}
          />
        ))}
        {dbOk === false && (
          <g transform="translate(485 51)" stroke={STATUS_COLORS.down} strokeWidth="3" strokeLinecap="round">
            <circle r="11" style={{ fill: "var(--app-card-bg)" }} />
            <path d="M -4.5 -4.5 L 4.5 4.5 M 4.5 -4.5 L -4.5 4.5" />
          </g>
        )}

        <g ref={fxLayer} />

        <Node {...NODES.you} ringColor="#38bdf8">
          <LaptopIcon />
        </Node>
        <Node {...NODES.server} ringColor={serverColor} breathe={status !== "checking"}>
          <ServerIcon />
        </Node>
        <Node {...NODES.db} ringColor={dbColor} breathe={dbOk === true}>
          <DatabaseIcon />
        </Node>

        {/* A small satellite circling the server while it's reachable. */}
        {!down && status !== "checking" && (
          <g className="sp-orbit">
            <circle cx={NODES.server.x} cy={NODES.server.y - NODE_R - 18} r="3.5" fill={serverColor} opacity="0.85" />
          </g>
        )}

        <g ref={packetLayer} />
      </svg>

      {/* Labels sit in HTML so they stay readable when the SVG scales down. */}
      <div className="relative mt-1 h-12 sm:h-11">
        {[
          { key: "you", left: (NODES.you.x / VB_W) * 100 },
          { key: "server", left: (NODES.server.x / VB_W) * 100 },
          { key: "db", left: (NODES.db.x / VB_W) * 100 },
        ].map(({ key, left }) => (
          <div
            key={key}
            className="absolute top-0 w-[32%] -translate-x-1/2 text-center"
            style={{ left: `${left}%` }}
          >
            <p className="truncate text-xs font-semibold sm:text-sm">{labels[key].title}</p>
            <p className="app-muted truncate text-[11px] tabular-nums sm:text-xs">{labels[key].sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- history

export const PingHistory = ({ samples, slots, intervalMs }) => {
  const padded = [
    ...Array.from({ length: Math.max(0, slots - samples.length) }, () => null),
    ...samples.slice(-slots),
  ];
  const good = samples.filter((s) => s.ms != null).map((s) => s.ms);
  const scaleMax = Math.max(150, ...good);
  const minutes = Math.round((slots * intervalMs) / 60000);
  // Phones show the newest 40 so each bar is still a visible width.
  const mobileFrom = slots - 40;

  return (
    <div>
      <div className="flex h-16 items-end gap-[2px] sm:gap-[3px]" aria-hidden="true">
        {padded.map((s, i) => {
          const hide = i < mobileFrom ? "hidden sm:block" : "";
          if (!s) {
            return (
              <span
                key={`empty-${i}`}
                className={`h-1.5 flex-1 rounded-full bg-[color:var(--app-hover)] ${hide}`}
              />
            );
          }
          const tone = toneFor(s.ms);
          const height =
            s.ms == null ? 100 : Math.max(14, Math.min(100, (s.ms / scaleMax) * 100));
          return (
            <span
              key={s.id}
              title={`${s.time} — ${s.ms != null ? `${s.ms} ms` : "no response"}`}
              className={`sp-bar-in flex-1 rounded-full ${LATENCY_TONES[tone].bar} ${
                tone === "fail" ? "opacity-90" : "opacity-80 hover:opacity-100"
              } ${hide}`}
              style={{ height: `${height}%` }}
            />
          );
        })}
      </div>
      <div className="app-muted mt-2 flex items-center justify-between text-[11px]">
        <span>
          <span className="sm:hidden">
            {Math.round((40 * intervalMs) / 60000)} min ago
          </span>
          <span className="hidden sm:inline">{minutes} min ago</span>
        </span>
        <span className="hidden flex-wrap items-center gap-3 sm:flex">
          {["fast", "ok", "slow", "fail"].map((key) => (
            <span key={key} className="inline-flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${LATENCY_TONES[key].bar}`} />
              {LATENCY_TONES[key].label}
              {LATENCY_TONES[key].max && LATENCY_TONES[key].max !== Infinity
                ? ` < ${LATENCY_TONES[key].max} ms`
                : ""}
            </span>
          ))}
        </span>
        <span>now</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- number

/** Renders `value` (a number or null) gliding from its previous value. */
export const TweenNumber = ({ value, suffix = "", duration = 450 }) => {
  const [shown, setShown] = useState(value);
  const fromRef = useRef(value);

  useEffect(() => {
    if (value == null || fromRef.current == null || prefersReducedMotion()) {
      fromRef.current = value;
      setShown(value);
      return undefined;
    }
    const from = fromRef.current;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const current = Math.round(from + (value - from) * easeInOut(t));
      setShown(current);
      fromRef.current = current;
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  return shown == null ? "—" : `${shown}${suffix}`;
};
